/**
 * Bot detection — ported from yoto-web's
 * `resources/assets/js/src/analytics/analytics-utils.ts` `detectBot()`.
 *
 * Wire values in `BotDetectionType` are the `info=` strings yoto sends on
 * `isbt` events; analytics-consumer / BI treat them as opaque labels, so they
 * must not drift.
 */

import { UAParser } from 'ua-parser-js';
import { getSdkStateOrNull } from '../state';

/** `info=` values on `/dye?type=isbt` — keep in lockstep with yoto-web. */
export const BotDetectionType = {
  OLD_PINTEREST_PK: 'palnpk',
  PINTEREST_BOT: 'pntrbot',
  TIKTOK_CRAWLER: 'ttkcrwl',
  TABOOLA_CRAWLER: 'tblcrwl',
  NO_USER_AGENT: 'noua',
  KNOWN_BOT: 'knwnbt',
  UNKNOWN_UA: 'uknwn_ua',
  NON_WIN_800x600: 'n_win_800x600',
  LINUX_NO_UTM: 'lin_no_utm',
  NO_UTM_360x600: 'no_utm_300x600',
  UA_PARSE_ERR: 'ua_parse_err',
  UNDER_210: 'u210',
  WIDTH_OVER_8000: 'wo8k',
  HEIGHT_OVER_5000: 'ho5k',
  HIGHT_OVER_1800_AND_EXACT_WIDTH: 'ho1800aew',
  HIDDEN_AND_EXACT_WIDTH: 'haew',
  BLOCKED_SCREEN_SIZE: 'bss',
  BLOCKED_WINDOW_SIZE: 'bws',
  BLACKLISTED_MBID: 'blmbid',
} as const;

export type BotDetectionType = (typeof BotDetectionType)[keyof typeof BotDetectionType];

const KNOWN_BOT_UA =
  /(bot|pingdom|crawl|spider|aolbuild|baidu|bingpreview|facebookexternalhit\/|ia_archiver|mediapartners-google|teoma|slurp|yandex|yahoo\x20ad\x20monitoring|ptst\/|chrome-lighthouse)/gi;

const BLOCKED_MBIDS = new Set(['uimwzcx7veuvxu9o', 't3k0ixaexqgrdrxs']);

const BLOCKED_SCREEN_SIZES: ReadonlyArray<readonly [number, number]> = [
  [2000, 2000],
  [2000, 1200],
];

const BLOCKED_WINDOW_SIZES: ReadonlyArray<readonly [number, number]> = [
  [2000, 2000],
  [1256, 605],
  [340, 335],
  [365, 522],
  [340, 423],
  [340, 295],
  [340, 383],
  [365, 482],
  [350, 335],
  [834, 1108],
  [980, 1445],
];

export interface DetectBotOptions {
  /** ISO country from website config — enables the pinterest/aln/PK rule. */
  countryCode?: string;
  /** Override `location.search` (tests). */
  search?: string;
}

function readQuery(search: string): URLSearchParams {
  try {
    return new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  } catch {
    return new URLSearchParams();
  }
}

function windowSize(): { width: number; height: number } {
  try {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return {
      width: Math.round(window.innerWidth || 0),
      height: Math.round(window.innerHeight || 0),
    };
  } catch {
    return { width: 0, height: 0 };
  }
}

function screenSize(): { width: number; height: number } {
  try {
    if (typeof screen === 'undefined') return { width: 0, height: 0 };
    return { width: screen.width || 0, height: screen.height || 0 };
  } catch {
    return { width: 0, height: 0 };
  }
}

function documentIsHidden(): boolean {
  try {
    if (typeof document === 'undefined') return false;
    const doc = document as Document & { webkitHidden?: boolean };
    return !!(doc.hidden || doc.webkitHidden);
  } catch {
    return false;
  }
}

/**
 * Classify the current page as a bot. Returns the yoto `info=` label, or
 * `false` when the traffic looks human. `?disbt=1` disables detection (yoto
 * parity — used by internal QA).
 */
export function detectBotType(opts: DetectBotOptions = {}): BotDetectionType | false {
  const search =
    opts.search ?? (typeof location !== 'undefined' ? location.search : '');
  const qs = readQuery(search);

  if (qs.get('disbt') === '1') return false;

  const utmSource = qs.get('utm_source') ?? '';
  const utmMedium = qs.get('utm_medium') ?? '';
  const mbid = qs.get('mbid') ?? '';
  const crawler = qs.get('crawler') ?? '';
  const country = opts.countryCode ?? getSdkStateOrNull()?.config.geo.country ?? '';

  if (utmSource === 'pinterest' && utmMedium === 'aln' && country === 'PK') {
    return BotDetectionType.OLD_PINTEREST_PK;
  }

  if (crawler.includes('tiktok_preloading')) {
    return BotDetectionType.TIKTOK_CRAWLER;
  }

  const { width: screenW, height: screenH } = screenSize();

  try {
    if (typeof navigator === 'undefined') return BotDetectionType.NO_USER_AGENT;

    const ua = navigator.userAgent;
    const uaLower = (ua || '').toLowerCase();

    if (uaLower.includes('pinterest.com/bot.html')) {
      return BotDetectionType.PINTEREST_BOT;
    }
    if (uaLower.includes('taboolabot')) {
      return BotDetectionType.TABOOLA_CRAWLER;
    }
    if (!ua) {
      return BotDetectionType.NO_USER_AGENT;
    }
    if (ua.match(KNOWN_BOT_UA)) {
      return BotDetectionType.KNOWN_BOT;
    }

    const parsed = new UAParser(ua).getResult();
    const browserName = parsed.browser.name ?? '';
    const osName = parsed.os.name ?? '';

    if (!browserName && !osName) {
      return BotDetectionType.UNKNOWN_UA;
    }

    const osLower = osName.toLowerCase();
    if (screenW === 800 && screenH === 600 && osLower !== 'windows') {
      return BotDetectionType.NON_WIN_800x600;
    }

    if (osLower.includes('linux') && (!utmSource || !utmMedium)) {
      return BotDetectionType.LINUX_NO_UTM;
    }

    if (!utmSource && !utmMedium && screenW === 360 && screenH === 600) {
      return BotDetectionType.NO_UTM_360x600;
    }

    if (BLOCKED_MBIDS.has(mbid)) {
      return BotDetectionType.BLACKLISTED_MBID;
    }
  } catch {
    return BotDetectionType.UA_PARSE_ERR;
  }

  const { width: winW, height: winH } = windowSize();

  if (winW < 210 || winH < 210) {
    return BotDetectionType.UNDER_210;
  }
  if (winW > 8000) {
    return BotDetectionType.WIDTH_OVER_8000;
  }
  if (winH > 5000) {
    return BotDetectionType.HEIGHT_OVER_5000;
  }

  if ((winW === 1040 || winW === 1050) && winH >= 1800) {
    return BotDetectionType.HIGHT_OVER_1800_AND_EXACT_WIDTH;
  }

  if (documentIsHidden() && (winW === 2000 || winW === 350 || winW === 340)) {
    return BotDetectionType.HIDDEN_AND_EXACT_WIDTH;
  }

  if (BLOCKED_SCREEN_SIZES.some(([w, h]) => w === screenW && h === screenH)) {
    return BotDetectionType.BLOCKED_SCREEN_SIZE;
  }

  if (BLOCKED_WINDOW_SIZES.some(([w, h]) => w === winW && h === winH)) {
    return BotDetectionType.BLOCKED_WINDOW_SIZE;
  }

  if (mbid.includes('uimwzcx7veuvxu9o')) {
    return BotDetectionType.BLACKLISTED_MBID;
  }

  return false;
}

export function isBotTraffic(opts?: DetectBotOptions): boolean {
  return detectBotType(opts) !== false;
}
